<template>
  <div class="c-engraving-text">
    <text-input
      ref="ring"
      v-model="userText"
      :placeholder="placeholderText"
      class="c-engraving-text__input t-heading-6 u-center-text"
      :style="textStyle"
      :maxlength="updateMaxLength"
      @keydown="updateCursorPositionKeyUp"
      @mouseup="updateCursorPosition"
      @dblclick="handleDoubleClick"
    />
    <div v-if="requiredText">
      <span class="t-heading-6--error">* Text is required.</span>
    </div>
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="charCountVisible"
      class="u-text-align-right"
      v-html="charCountAndError"
    />
    <!-- eslint-enable vue/no-v-html -->
  </div>
</template>
<script>
import TextInput from '../inputs/TextInput'
import { engravingAllowListRegExp } from '../../utils/engravingAllowList'
import get from 'lodash/get'

export default {
  name: 'EngravingTextInput',
  components: {
    TextInput
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    font: {
      type: String,
      default: ''
    },
    cursorPosition: {
      type: Object,
      default: ()=>{return {charLocation:0, lineLocation:0}},
    },
    placeholderText:{
      type:String,
      default: 'Enter Text Here'
    },
    lengthLimit: {
      type: Number,
      default: 10
    },
    focus: {
      type: Boolean,
      default: false
    },
    requiredText: {
      type: Boolean,
      default: false
    },
    allowedSymbols: {
      type: Array,
      default: () => []
    },
    lineNumber: {
      type: Number,
      default: 0
    },
    charCountVisible: {
      type: Boolean,
      default: true
    },
    currentTheme:{
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      blurred: false,
      charWeight: 2
    }
  },
  computed: {
    /**
     * cache this RegExp object so it doesn't need to be constructed every time
     * the input changes
     */
    allowListRegex() {
      const font = this.font
      const fontKey = (font && font.includes('Enso')) ? font : `Enso ${font}`
      const allowedSymbols = this.allowedSymbols
      return engravingAllowListRegExp({
        fontKey,
        allowedSymbols
      })
    },
    updateMaxLength(){
      const value = this.value
      let val = RegExp(`[${this.allowedSymbols.join('')}]`,'g')
      let match = value.match(val);
      let matchLength = match != null ? this.lengthLimit - ( match.length ) : this.lengthLimit
      return matchLength
    },
    userText: {
      get() {
        const textVal = this.value
        const currentTheme = get(this.currentTheme, 'characterReduction')
        let lengthLimit = currentTheme ? this.lengthLimit - currentTheme : this.lengthLimit
        let val = RegExp(`[${this.allowedSymbols.join('')}]`,'g')
        const specialChars = textVal.match(val);
        const specLength = specialChars != null ? specialChars.length : 0
        let result = ''
        if (textVal && (textVal.length <= lengthLimit)) {
          result = textVal
        } else if (textVal && (textVal.length + specLength > lengthLimit)) {
          result = textVal.substring(0, lengthLimit - specLength)
        }
        return result.replace(this.allowListRegex, '')
      },
      set(value) {
        this.$emit('input', value.replace(this.allowListRegex, ''),this.lineNumber)
      }
    },
    cursor() {
      const cursorPosition = this.cursorPosition
      const textVal = this.value
      let result = 0
      if (
        cursorPosition &&
        cursorPosition.charLocation &&
        cursorPosition.charLocation >= 0 &&
        cursorPosition.charLocation <= textVal.length
      ) {
        result = cursorPosition.charLocation
      }
      return result
    },
    charCountAndError(){
      const value = this.value
      const currentTheme = get(this.currentTheme, 'characterReduction') || 0
      const lengthLimit = this.lengthLimit
      let userTextLength = parseInt(this.userText.length)

      let val = RegExp(`[${this.allowedSymbols.join('')}]`,'g')      
      const specialChars = value.match(val);

      let message = `${lengthLimit} characters left`
      if (this.userText) {
        if(this.userText){
          let specCharsLength = specialChars != null ? specialChars.length : 0;
          message = `${lengthLimit - (userTextLength + specCharsLength) - currentTheme} characters left`
          if (lengthLimit <= this.userText.length + specCharsLength) {
            message = `<span class="t-heading-6--error">${message}</span>`
          }
        }
      }
      return message
    },
    textStyle() {
      return {
        'font-family': this.font
      }
    }
  },
  watch:{
    cursorPosition: {
      handler: function () {
        this.setCaretPosition()
      },
      deep:true
    },
    focus() {
      this.setCaretPosition()
    },
    charCountAndError(value) {
      this.$emit('charCount', value)
    },
  },
  mounted() {
    if (this.focus) {
      this.setCaretPosition()
    }
    if (
      this.userText &&
      (this.userText.length > this.lengthLimit)
    ) {
      this.userText = this.userText.substring(0, this.lengthLimit)
    }
  },
  methods:{
    setCaretPosition() {
      if(this.cursorPosition.lineLocation == this.lineNumber) {
        this.$refs.ring.focus()
        this.$nextTick(() => {
          this.$refs.ring.setSelectionRange(
            this.cursorPosition.charLocation,
            this.cursorPosition.charLocation
          )
        })
      }
    },
    charCount(){
      let val = RegExp(`[${this.allowedSymbols.join('')}]`,'g')
      let charArr = this.userText.match(val);
      return charArr != null ? charArr.length * this.charWeight : 0
    },
    updateCursorPosition(event) {
      this.$emit('cursorPosition',{ charLocation:event.target.selectionStart, lineLocation:this.lineNumber})
    },
    updateCursorPositionKeyUp(event) {
      let position = 1;
      const { target, key, keyCode } = event;
      // @link <https://stackoverflow.com/questions/45433810/>
      // @link <https://stackoverflow.com/questions/18059259/>
      if (
        (key === 'Backspace') ||
        (keyCode === 8)
      ) {
        // Allow default behavior for backspace key
        return;
      }

      if (target && target.selectionStart) {
        position = target.selectionStart;

        if (position <= 1 && key !== 'ArrowLeft') {
          position += 1;
        }
      }
      this.$emit('cursorPosition',{ charLocation:position, lineLocation:this.lineNumber})
    },
    handleDoubleClick(){
      if (this.$refs.ring && this.$refs.ring.$el.children && this.$refs.ring.$el.children[0]) {
        this.$refs.ring.$el.children[0].select();
      }
    }
  }
}

</script>
<style lang="scss">
  .c-engraving-text {
    width: 100%;
    .t-heading-7 {
      line-height: 24px;
    }
  }
</style>
